/**
 * PromiseArrivalSection.jsx — GRANULLO_PROMISE_TO_ARRIVAL_CINEMATIC_REBUILD_V1
 *
 * 3-STATE CINEMATIC INTERACTION SYSTEM:
 * STATE 1: THE PROMISE — polished institutional declarations
 * STATE 2: THE COLLAPSE — what actually happened (tear reveal)
 * STATE 3: THE REPAIR — Granullo's verified path
 *
 * Each card cycles through states on click.
 * Bottom: "How this scales to millions" card grid.
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Coins, Leaf, Shield, Globe, Heart, Package, Building2, Briefcase, Wallet, TrendingUp, Rocket, Bot, Truck, HandCoins, GraduationCap, Receipt, FileText, Zap, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';

const RED = '#CD2200';
const AMBER = '#D97706';
const GREEN = '#22C55E';
const BLUE = '#6B6B6B';

/* ═══ STORY DATA — 3 STATES PER CARD ═══ */
const STORIES = [
  {
    id: 'education',
    category: 'Education',
    promise: {
      headline: 'School voucher approved for 12,000 families',
      detail: 'Government allocation locked for academic year 2026',
      badge: 'APPROVED',
    },
    collapse: {
      headline: '43% held in verification queues',
      stats: ['4 intermediaries delayed release', 'No settlement confirmation', 'No arrival proof'],
      human: 'School year started before voucher arrived.',
    },
    repair: {
      steps: ['Intent verified', 'Conditions matched', 'Beneficiary confirmed', 'Partial release', 'Twin memory', 'Arrival confirmed'],
      result: 'Child receives tuition directly at verified school',
    },
  },
  {
    id: 'agriculture',
    category: 'Agriculture',
    promise: {
      headline: 'Farmer subsidy announced — $420M allocated',
      detail: 'Ministry approval for Kharif season direct benefit transfer',
      badge: 'ALLOCATED',
    },
    collapse: {
      headline: '18% ghost recipients identified',
      stats: ['3 district offices delayed processing', 'Duplicate beneficiary records', 'Manual verification backlog'],
      human: 'Crop season missed.',
    },
    repair: {
      steps: ['Intent verified', 'Beneficiary matched', 'Account validated', 'Direct transfer', 'Twin memory', 'Arrival confirmed'],
      result: 'Money lands in verified wallet within 48 hours',
    },
  },
  {
    id: 'health',
    category: 'Healthcare',
    promise: {
      headline: 'Health aid approved for rural clinics',
      detail: '$180M allocated for essential medicine procurement',
      badge: 'FUNDED',
    },
    collapse: {
      headline: 'Clinic stocked only 40%',
      stats: ['Procurement delays at state level', 'Missing distribution tracking', 'No last-mile verification'],
      human: 'Clinic stocked only 40%.',
    },
    repair: {
      steps: ['Intent verified', 'Supplier matched', 'Delivery scheduled', 'Pharmacy confirmed', 'Twin memory', 'Arrival verified'],
      result: 'Medicine paid directly at pharmacy counter',
    },
  },
  {
    id: 'welfare',
    category: 'Welfare',
    promise: {
      headline: 'Welfare allocation budgeted — fiscal year locked',
      detail: '$2.1B committed for family support programs nationwide',
      badge: 'COMMITTED',
    },
    collapse: {
      headline: '28% verified at destination',
      stats: ['Reconciliation mismatch across states', 'Non-verifiable intermediary spend', 'Settlement delays > 90 days'],
      human: 'Family still waiting.',
    },
    repair: {
      steps: ['Intent verified', 'Family matched', 'Entitlement calculated', 'Direct disbursement', 'Twin memory', 'Arrival confirmed'],
      result: 'Family receives exact entitlement, verified',
    },
  },
];

/* ═══ SCALE CASES — real-world use cases across 6 categories ═══ */
const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'public', label: 'Public Sector' },
  { id: 'csr', label: 'CSR & Donations' },
  { id: 'corporate', label: 'Corporate' },
  { id: 'trade', label: 'Trade & Commerce' },
  { id: 'startup', label: 'Startup / VC' },
  { id: 'ai', label: 'AI Agents' },
];

const SCALE_CASES = [
  /* ─── PUBLIC SECTOR ─── */
  {
    category: 'public', icon: Users, label: 'UBI / UHI',
    desc: 'Programmable universal income with dignity-preserving constraints',
    accent: 'millions safely',
    scenario: 'State disburses $800/month to 2.1M residents',
    flow: [
      { label: 'Eligibility proof', detail: 'residency + age + consent verified' },
      { label: 'Payout to wallet', detail: 'direct to recipient, no handler' },
      { label: 'Spend constraints', detail: 'essentials allowed, casinos blocked' },
    ],
    impossible: 'Duplicate identities. Middleman skim. Untraceable disbursement.',
  },
  {
    category: 'public', icon: Coins, label: 'CBDC Disbursements',
    desc: 'Digital currency with built-in governance at the protocol level',
    accent: 'programmable welfare',
    scenario: 'Central bank issues $3B in post-disaster relief',
    flow: [
      { label: 'Geo-fence unlock', detail: 'valid only within affected zones' },
      { label: 'Category allowance', detail: 'food, shelter, medicine — time-boxed' },
      { label: 'Auto-expiry', detail: 'unspent funds return to treasury at T+90d' },
    ],
    impossible: 'Diversion to non-relief spend. Political reassignment. Silent leakage.',
  },
  {
    category: 'public', icon: Leaf, label: 'Climate Dividends',
    desc: 'Carbon credit payouts verified against real-world reductions',
    accent: 'climate dividends',
    scenario: 'Fund pays $12M to reforestation cooperatives',
    flow: [
      { label: 'Satellite proof', detail: 'canopy growth confirmed via imagery' },
      { label: 'Sensor attestation', detail: 'soil + moisture sensors cross-signed' },
      { label: 'Payout on verification', detail: 'only validated hectares disbursed' },
    ],
    impossible: 'Paper credits. Double-counted hectares. Unverified claims.',
  },
  {
    category: 'public', icon: Shield, label: 'Programmable Subsidies',
    desc: 'Government benefits that enforce purpose without surveillance',
    accent: 'welfare governance',
    scenario: 'Nation grants $420M farmer fertilizer subsidy',
    flow: [
      { label: 'Beneficiary match', detail: 'land records + crop season verified' },
      { label: 'Vendor whitelist', detail: 'redeem only at licensed agri-shops' },
      { label: 'Per-farm cap', detail: 'enforced structurally — no bundling' },
    ],
    impossible: 'Ghost farmers. Resale on grey markets. Inflated invoices.',
  },
  {
    category: 'public', icon: GraduationCap, label: 'Education Vouchers',
    desc: 'Tuition funds that reach institutions, not intermediaries',
    accent: 'direct reach',
    scenario: '12,000 scholarships routed to accredited schools',
    flow: [
      { label: 'School accreditation', detail: 'real-time registry check' },
      { label: 'Attendance trigger', detail: 'monthly release on enrollment proof' },
      { label: 'Outcome reporting', detail: 'progression data signed by institution' },
    ],
    impossible: 'Payment to phantom institutions. Family diversion. Post-hoc audit gaps.',
  },
  {
    category: 'public', icon: Heart, label: 'Health & Aid',
    desc: 'Insurance payouts and humanitarian aid locked to recovery milestones',
    accent: 'verified impact',
    scenario: 'Insurer pays $58M post-catastrophe to 18,000 households',
    flow: [
      { label: 'Damage assessment', detail: 'ground-truth + drone survey signed' },
      { label: 'Recipient identity', detail: 'biometric + address verified' },
      { label: 'Milestone release', detail: 'rebuild, restock, reopen — each proven' },
    ],
    impossible: 'False claims. Duplicate disbursement. Aid capture by intermediaries.',
  },

  /* ─── CSR & DONATIONS ─── */
  {
    category: 'csr', icon: HandCoins, label: 'Foundation Grants',
    desc: 'Donor funds released only on verified field outcomes',
    accent: 'impact not intent',
    scenario: 'Foundation distributes $25M to 340 NGOs globally',
    flow: [
      { label: 'Grantee vetting', detail: 'legal + financial + reputational checks' },
      { label: 'Milestone reports', detail: 'photos + signatures + field data' },
      { label: 'Release on proof', detail: 'next tranche only after prior verified' },
    ],
    impossible: 'Funds sitting idle. Unreported outcomes. Ghost beneficiaries.',
  },
  {
    category: 'csr', icon: Globe, label: 'Disaster Relief Funds',
    desc: 'Public donations routed directly to verified survivors',
    accent: 'no skim, no delay',
    scenario: 'Public raises $40M in 72 hours for flood victims',
    flow: [
      { label: 'Victim registry', detail: 'gov + NGO + satellite cross-signed' },
      { label: 'Direct wallet', detail: 'funds bypass admin intermediaries' },
      { label: 'Category-locked', detail: 'food, water, shelter — not politics' },
    ],
    impossible: 'Admin overhead siphons. Politically captured allocation.',
  },
  {
    category: 'csr', icon: Users, label: 'Employee Gift Matching',
    desc: 'Corporate donation matching verified at recipient charity',
    accent: 'matched, not mapped',
    scenario: 'Corporate matches $8M of employee donations in 2025',
    flow: [
      { label: 'Employee signal', detail: 'donation verified at source charity' },
      { label: 'Charity verification', detail: 'real entity, tax-deductible, active' },
      { label: 'Instant match', detail: 'matched dollar arrives same block' },
    ],
    impossible: 'Fake charity receipts. Delayed or forgotten matches.',
  },
  {
    category: 'csr', icon: GraduationCap, label: 'Scholarship Programs',
    desc: 'Tuition programs where funds never touch the family',
    accent: 'reaches the student',
    scenario: 'Corporate scholarship program pays $18M to universities',
    flow: [
      { label: 'Student enrollment', detail: 'university registry real-time' },
      { label: 'Direct-to-bursar', detail: 'never routed through intermediaries' },
      { label: 'Persistence-gated', detail: 'next semester on GPA + enrollment' },
    ],
    impossible: 'Family diversion. Over-payment. Phantom enrollment.',
  },

  /* ─── CORPORATE FINANCE ─── */
  {
    category: 'corporate', icon: Receipt, label: 'Expense Compliance',
    desc: 'Corporate cards that enforce policy at swipe, not month-end',
    accent: 'no expense surprises',
    scenario: 'Enterprise issues 2,400 cards with per-role policy',
    flow: [
      { label: 'Role-based rules', detail: 'travel, meals, SaaS — per role' },
      { label: 'Merchant allowlist', detail: 'only approved vendors accept' },
      { label: 'Auto-categorization', detail: 'GL coded at authorization time' },
    ],
    impossible: 'Policy violations. Month-end reconciliation drift. Lost receipts.',
  },
  {
    category: 'corporate', icon: Building2, label: 'Vendor Payments',
    desc: 'Accounts payable that releases only on delivery + quality proof',
    accent: 'paid on proof',
    scenario: 'Enterprise AP processes $2.1B across 8,000 vendors',
    flow: [
      { label: 'PO ↔ Invoice match', detail: 'structural — no OCR ambiguity' },
      { label: 'Delivery attestation', detail: 'receiving system cross-signed' },
      { label: 'Release on quality', detail: 'QA passing gates final payment' },
    ],
    impossible: 'Duplicate invoices. Pay-before-delivery. Vendor fraud.',
  },
  {
    category: 'corporate', icon: Wallet, label: 'Treasury Governance',
    desc: 'Multi-sig protocols for capital movement at enterprise scale',
    accent: 'no rogue transfers',
    scenario: 'CFO governs $1.4B operating treasury',
    flow: [
      { label: 'Approval quorum', detail: 'CFO + Treasurer + Board for >$5M' },
      { label: 'Counterparty check', detail: 'sanctioned entity list live' },
      { label: 'Rate + timing rules', detail: 'execution window enforced' },
    ],
    impossible: 'Single-signer movement. Rogue wire transfers. Manual error.',
  },
  {
    category: 'corporate', icon: Briefcase, label: 'M&A Escrow',
    desc: 'Acquisition funds released only on integration milestone proofs',
    accent: 'escrow that knows',
    scenario: '$340M acquisition with 6-quarter earnout',
    flow: [
      { label: 'Revenue milestones', detail: 'audited financial feeds signed' },
      { label: 'Retention targets', detail: 'key employees still active at T+12' },
      { label: 'Integration gates', detail: 'systems + ops fully merged' },
    ],
    impossible: 'Premature release. Disputed earnouts. Silent clawbacks.',
  },

  /* ─── TRADE & COMMERCE ─── */
  {
    category: 'trade', icon: Package, label: 'Trade Finance / LC',
    desc: 'Letters of credit release only when documentary and event truth clears',
    accent: 'global commerce',
    scenario: 'Bank releases $40M LC for cross-border shipment',
    flow: [
      { label: 'Document truth', detail: 'BL, COO, sanctions, customs all clear' },
      { label: 'Shipment event', detail: 'port arrival + cargo inspection signed' },
      { label: 'Release on proof', detail: 'capital moves the moment truth lands' },
    ],
    impossible: 'Double-financing. Fraudulent BL. Premature release against incomplete docs.',
  },
  {
    category: 'trade', icon: Truck, label: 'Supply Chain Escrow',
    desc: 'B2B payments held until delivery + quality at destination',
    accent: 'paid on arrival',
    scenario: 'Retailer escrows $95M in Q4 supplier payments',
    flow: [
      { label: 'Shipment tracking', detail: 'IoT + carrier signals synchronized' },
      { label: 'QC at destination', detail: 'inspection report cryptographically signed' },
      { label: 'Tranched release', detail: '70% on delivery, 30% on sell-through' },
    ],
    impossible: 'Paying for damaged goods. Lost-in-transit write-offs. Disputes.',
  },
  {
    category: 'trade', icon: Globe, label: 'Cross-Border B2B',
    desc: 'International settlement without correspondent bank friction',
    accent: 'corridor by corridor',
    scenario: 'Importer settles $28M/month across 14 corridors',
    flow: [
      { label: 'FX rate lock', detail: 'rate frozen at contract, not settlement' },
      { label: 'Sanctions + AML', detail: 'every hop verified structurally' },
      { label: 'Arrival-triggered', detail: 'funds move on proof of delivery' },
    ],
    impossible: 'FX surprise losses. Sanctions pass-through. Opaque intermediary fees.',
  },
  {
    category: 'trade', icon: Coins, label: 'Commodity Settlement',
    desc: 'Physical commodity trades that settle on verified delivery',
    accent: 'barrel to bank',
    scenario: 'Energy major settles $180M crude trade',
    flow: [
      { label: 'Quality assay', detail: 'independent lab results signed' },
      { label: 'Quantity proof', detail: 'metered delivery at terminal verified' },
      { label: 'Settlement price', detail: 'benchmark at agreed delivery window' },
    ],
    impossible: 'Quality disputes. Phantom barrels. Settlement-date arbitrage.',
  },

  /* ─── STARTUP / VC ─── */
  {
    category: 'startup', icon: Rocket, label: 'Milestone Tranches',
    desc: 'Venture funding released on structural proof of traction',
    accent: 'tranches on truth',
    scenario: 'Series B $30M tranched across 4 milestones',
    flow: [
      { label: 'Revenue proof', detail: 'billing system signals + bank verification' },
      { label: 'User metric', detail: 'retention + active users signed' },
      { label: 'Hire milestone', detail: 'key roles filled + active' },
    ],
    impossible: 'Vanity metrics. Milestone fabrication. Mid-tranche disputes.',
  },
  {
    category: 'startup', icon: TrendingUp, label: 'Grant Disbursements',
    desc: 'Accelerator and grant capital tied to program deliverables',
    accent: 'earned, not given',
    scenario: 'Accelerator deploys $2.4M across 40 startups',
    flow: [
      { label: 'Program enrollment', detail: 'cohort + module completion tracked' },
      { label: 'Deliverable proof', detail: 'MVP demo + mentor sign-off' },
      { label: 'Follow-on trigger', detail: 'next check on revenue threshold' },
    ],
    impossible: 'No-show grantees. Program drop-outs still paid. Forgotten deliverables.',
  },
  {
    category: 'startup', icon: FileText, label: 'SAFE / Note Conversion',
    desc: 'Conversion events triggered automatically at priced round close',
    accent: 'auto-convert',
    scenario: '180 SAFEs convert at $80M cap equity round',
    flow: [
      { label: 'Round close signal', detail: 'cap table system + counsel signed' },
      { label: 'Cap calculation', detail: 'per-SAFE terms evaluated structurally' },
      { label: 'Share issuance', detail: 'equity allocated atomically, no errors' },
    ],
    impossible: 'Manual conversion errors. Missing SAFEs. Cap table disputes.',
  },
  {
    category: 'startup', icon: Users, label: 'Employee Equity',
    desc: 'Vesting, cliff enforcement, and exercise windows governed by code',
    accent: 'vesting by structure',
    scenario: 'Startup manages 240 employee equity grants',
    flow: [
      { label: 'Cliff verification', detail: 'employment status at 12-month mark' },
      { label: 'Monthly vest', detail: 'proportional, terminated-aware' },
      { label: 'Exercise window', detail: '90-day window enforced post-departure' },
    ],
    impossible: 'Missed cliff. Over-vested grants. Expired-but-exercised options.',
  },

  /* ─── AI AGENTS ─── */
  {
    category: 'ai', icon: Bot, label: 'Agent Spend Limits',
    desc: 'Autonomous AI actions bounded by structural spending rules',
    accent: 'bounded autonomy',
    scenario: 'Enterprise deploys 400 AI agents with $100-$10K budgets',
    flow: [
      { label: 'Per-agent cap', detail: 'role-based daily + monthly limits' },
      { label: 'Category gates', detail: 'infra, data, APIs — per agent' },
      { label: 'Escalation trigger', detail: '>$5K requires human approval' },
    ],
    impossible: 'Runaway spend. Cross-agent collusion. Budget-busting loops.',
  },
  {
    category: 'ai', icon: Zap, label: 'Agentic Commerce',
    desc: 'AI agents purchasing autonomously with verifiable intent',
    accent: 'proof-of-intent',
    scenario: 'AI shopping agent executes 12M transactions/month',
    flow: [
      { label: 'User intent hash', detail: 'purchase goal signed at task-start' },
      { label: 'Merchant whitelist', detail: 'only verified sellers accept' },
      { label: 'Price bound check', detail: 'within 5% of market at execution' },
    ],
    impossible: 'Off-intent purchases. Price manipulation. Counterfeit vendors.',
  },
  {
    category: 'ai', icon: Coins, label: 'API Pay-Per-Use',
    desc: 'Machine-to-machine micro-payments gated by usage truth',
    accent: 'metered by machine',
    scenario: 'Model APIs serve 80M calls/day with per-token billing',
    flow: [
      { label: 'Usage meter', detail: 'tokens consumed verified at source' },
      { label: 'Tiered pricing', detail: 'volume + tier computed per-request' },
      { label: 'Instant settlement', detail: 'sub-cent payment per call' },
    ],
    impossible: 'Usage disputes. Month-end reconciliation. Revenue leakage.',
  },
  {
    category: 'ai', icon: Wallet, label: 'Autonomous Treasury',
    desc: 'AI-managed treasury operations within structural guardrails',
    accent: 'governed autonomy',
    scenario: 'AI treasury bot manages $80M across 12 accounts',
    flow: [
      { label: 'Rebalance rules', detail: 'target allocations within bands' },
      { label: 'Counterparty list', detail: 'approved institutions only' },
      { label: 'Human checkpoint', detail: 'weekly override window available' },
    ],
    impossible: 'Unauthorized counterparties. Out-of-band rebalances. Policy drift.',
  },
];

/* ═══ INTERACTIVE SCALE CASE CARD ═══ */
const ScaleCaseCard = ({ fc, i, isOpen, onToggle }) => {
  const Icon = fc.icon;
  return (
    <motion.button
      type="button"
      onClick={onToggle}
      className="group text-left w-full p-5 rounded-xl transition-all duration-300 cursor-pointer"
      style={{
        background: 'var(--shell-surface)',
        border: `1px solid ${isOpen ? `${RED}40` : 'var(--shell-border)'}`,
        boxShadow: isOpen
          ? `0 4px 14px rgba(205,34,0,0.10), 0 12px 40px rgba(205,34,0,0.06)`
          : '0 1px 2px rgba(15,15,15,0.02)',
        gridColumn: isOpen ? '1 / -1' : 'auto',
      }}
      layout
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: i * 0.05, layout: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } }}
      data-testid={`scale-case-${fc.label.toLowerCase().replace(/[\s/]+/g, '-')}`}
    >
      <motion.div layout="position" className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg" style={{ background: `${RED}10` }}>
            <Icon size={15} style={{ color: RED }} />
          </span>
          <span className="text-[14px] font-semibold" style={{ fontFamily: '"Inter Tight", sans-serif', color: 'var(--shell-text)' }}>
            {fc.label}
          </span>
        </div>
        <span
          className="inline-flex items-center justify-center w-6 h-6 rounded-full"
          style={{
            background: isOpen ? RED : 'var(--shell-bg)',
            color: isOpen ? '#FFFFFF' : 'var(--shell-text-muted)',
            border: `1px solid ${isOpen ? RED : 'var(--shell-border)'}`,
            transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
            transition: 'transform 240ms cubic-bezier(0.2,0.8,0.2,1), background 240ms ease, color 240ms ease',
          }}
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </span>
      </motion.div>

      <motion.p layout="position" className="text-[11.5px] leading-relaxed mb-2" style={{ color: 'var(--shell-text-secondary)' }}>
        {fc.desc}
      </motion.p>
      <motion.span layout="position" style={{ fontFamily: '"Caveat", cursive', color: RED, fontSize: 13, opacity: 0.78 }}>
        {fc.accent}
      </motion.span>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="expand"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <div className="mt-5 pt-5 border-t grid grid-cols-1 lg:grid-cols-[1fr_1.2fr_1fr] gap-6" style={{ borderColor: 'var(--shell-border)' }}>
              <div>
                <div className="text-[9px] font-mono uppercase tracking-[0.15em] mb-2" style={{ color: 'var(--shell-text-muted)' }}>Real-world scenario</div>
                <p className="text-[13.5px] font-medium leading-snug" style={{ color: 'var(--shell-text)', fontFamily: '"Inter Tight", sans-serif' }}>
                  {fc.scenario}
                </p>
              </div>
              <div>
                <div className="text-[9px] font-mono uppercase tracking-[0.15em] mb-3" style={{ color: 'var(--shell-text-muted)' }}>How money behaves</div>
                <div className="space-y-2.5">
                  {fc.flow.map((step, si) => (
                    <div key={si} className="flex items-start gap-2.5">
                      <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full text-[9px] font-semibold flex-shrink-0 mt-[1px]" style={{ background: `${RED}14`, color: RED, fontFamily: '"SF Mono", monospace' }}>
                        {si + 1}
                      </span>
                      <div className="min-w-0">
                        <div className="text-[12px] font-medium leading-tight" style={{ color: 'var(--shell-text)' }}>{step.label}</div>
                        <div className="text-[10.5px] leading-tight mt-0.5" style={{ color: 'var(--shell-text-secondary)' }}>{step.detail}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[9px] font-mono uppercase tracking-[0.15em] mb-2" style={{ color: `${RED}CC` }}>What becomes impossible</div>
                <ul className="space-y-1.5">
                  {fc.impossible.split('. ').filter(Boolean).map((item, ii) => (
                    <li key={ii} className="flex items-start gap-2 text-[11.5px] leading-snug" style={{ color: 'var(--shell-text-secondary)' }}>
                      <XCircle size={10} style={{ color: RED, opacity: 0.7, marginTop: 3, flexShrink: 0 }} />
                      <span>{item.replace(/\.$/, '')}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
};

const StoryCard = ({ story, index }) => {
  const [state, setState] = useState(0); // 0=promise, 1=collapse, 2=repair

  const nextState = () => setState((s) => (s + 1) % 3);

  return (
    <motion.div
      className="relative rounded-xl overflow-hidden cursor-pointer select-none group"
      style={{
        background: state === 0 ? 'var(--shell-surface, #FFFFFF)' : state === 1 ? 'var(--shell-surface, #0A0606)' : 'var(--shell-surface, #060A08)',
        border: `1px solid ${state === 0 ? 'var(--border-soft, rgba(15,15,15,0.08))' : state === 1 ? `${RED}30` : `${GREEN}20`}`,
        minHeight: 264,
        boxShadow: state === 0
          ? '0 1px 2px rgba(15,15,15,0.03), 0 4px 16px rgba(15,15,15,0.04)'
          : 'none',
        transition: 'box-shadow 280ms cubic-bezier(0.2,0.8,0.2,1), border-color 280ms ease',
      }}
      onClick={nextState}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08, duration: 0.5 }}
      whileHover={{ y: -2, boxShadow: state === 0 ? '0 2px 4px rgba(15,15,15,0.04), 0 12px 32px rgba(205,34,0,0.08)' : undefined }}
      whileTap={{ scale: 0.995 }}
      data-testid={`story-card-${story.id}`}
    >
      <AnimatePresence mode="wait">
        {/* STATE 1: PROMISE */}
        {state === 0 && (
          <motion.div
            key="promise"
            className="p-5 h-full flex flex-col justify-between"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20, filter: 'blur(4px)' }}
            transition={{ duration: 0.35 }}
          >
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[9px] font-mono uppercase tracking-[0.15em]" style={{ color: 'var(--shell-text-secondary)' }}>
                  {story.category}
                </span>
                <span className="text-[8px] px-2 py-0.5 rounded-full font-mono font-medium" style={{ background: 'var(--shell-surface)', color: 'var(--shell-text-secondary, var(--shell-text-secondary))' }}>
                  {story.promise.badge}
                </span>
              </div>
              <h3 className="text-[16px] sm:text-[18px] font-semibold leading-tight mb-2" style={{ color: 'var(--shell-text)', fontFamily: '"Inter Tight", sans-serif' }}>
                {story.promise.headline}
              </h3>
              <p className="text-[11px] leading-relaxed" style={{ color: 'var(--shell-text-secondary)' }}>
                {story.promise.detail}
              </p>

              {/* Ghost timeline — fills the visual space with promise→???→arrival */}
              <div className="mt-5 flex items-center gap-2.5">
                <div className="flex items-center gap-1.5">
                  <span className="w-[6px] h-[6px] rounded-full" style={{ background: 'var(--shell-text-secondary)', opacity: 0.9 }} />
                  <span className="text-[9px] font-mono uppercase tracking-[0.08em]" style={{ color: 'var(--shell-text-secondary)' }}>Promised</span>
                </div>
                <div className="flex-1 h-px" style={{ background: 'linear-gradient(to right, var(--shell-text-secondary), transparent)', opacity: 0.3 }} />
                <div className="flex items-center gap-1.5">
                  <span className="w-[6px] h-[6px] rounded-full border border-dashed" style={{ borderColor: `${RED}60`, background: 'transparent' }} />
                  <span className="text-[9px] font-mono uppercase tracking-[0.08em]" style={{ color: `${RED}90`, fontStyle: 'italic' }}>Unknown</span>
                </div>
              </div>
            </div>

            <div className="flex items-end justify-between mt-4">
              <span className="-rotate-1 leading-none" style={{ fontFamily: '"Caveat", cursive', fontSize: 15, color: `${RED}B0` }}>
                looked like it would arrive
              </span>
              {/* REAL BUTTON — inline pill with hover state */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); nextState(); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-medium tracking-[0.02em] transition-all duration-200 hover:shadow-sm active:scale-[0.97]"
                style={{
                  background: 'var(--shell-text, #0A0A0A)',
                  color: 'var(--shell-bg, #FFFFFF)',
                  border: '1px solid var(--shell-text, #0A0A0A)',
                }}
                data-testid={`reveal-truth-${story.id}`}
              >
                See what happened
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                </svg>
              </button>
            </div>
          </motion.div>
        )}

        {/* STATE 2: COLLAPSE */}
        {state === 1 && (
          <motion.div
            key="collapse"
            className="p-5 h-full flex flex-col justify-between"
            initial={{ opacity: 0, scale: 0.97, filter: 'blur(6px)' }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, x: 20, filter: 'blur(4px)' }}
            transition={{ duration: 0.4 }}
          >
            {/* Fracture lines */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <div className="absolute top-0 left-[30%] w-px h-full" style={{ background: `${RED}15` }} />
              <div className="absolute top-0 left-[65%] w-px h-full" style={{ background: `${RED}10` }} />
              <div className="absolute top-[40%] left-0 w-full h-px" style={{ background: `${RED}12` }} />
            </div>

            <div className="relative z-[1]">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={12} style={{ color: RED }} />
                <span className="text-[9px] font-mono uppercase tracking-[0.15em]" style={{ color: RED }}>
                  What actually happened
                </span>
              </div>
              <h3 className="text-[16px] sm:text-[18px] font-semibold leading-tight mb-3" style={{ color: RED, fontFamily: '"Inter Tight", sans-serif' }}>
                {story.collapse.headline}
              </h3>
              <div className="space-y-1.5">
                {story.collapse.stats.map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <XCircle size={9} style={{ color: `${AMBER}90` }} />
                    <span className="text-[10px]" style={{ color: `${AMBER}CC` }}>{s}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative z-[1] mt-4 flex items-end justify-between">
              <span className="-rotate-1 leading-none" style={{ fontFamily: '"Caveat", cursive', fontSize: 15, color: RED }}>
                "{story.collapse.human}"
              </span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); nextState(); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-medium tracking-[0.02em] transition-all duration-200 hover:shadow-sm active:scale-[0.97]"
                style={{
                  background: RED,
                  color: '#FFFFFF',
                  border: `1px solid ${RED}`,
                  boxShadow: `0 1px 2px ${RED}40`,
                }}
                data-testid={`see-repair-${story.id}`}
              >
                See the repair
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                </svg>
              </button>
            </div>
          </motion.div>
        )}

        {/* STATE 3: REPAIR */}
        {state === 2 && (
          <motion.div
            key="repair"
            className="p-5 h-full flex flex-col justify-between"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.4 }}
          >
            <div>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={12} style={{ color: GREEN }} />
                <span className="text-[9px] font-mono uppercase tracking-[0.15em]" style={{ color: `${GREEN}90` }}>
                  Granullo verified path
                </span>
              </div>
              {/* Repair flow */}
              <div className="space-y-1">
                {story.repair.steps.map((step, i) => (
                  <motion.div
                    key={step}
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.06, duration: 0.3 }}
                  >
                    <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: i < story.repair.steps.length - 1 ? `${BLUE}60` : GREEN }} />
                    <span className="text-[10px] font-mono" style={{ color: i < story.repair.steps.length - 1 ? 'var(--shell-text-muted)' : `${GREEN}CC` }}>
                      {step}
                    </span>
                    {i < story.repair.steps.length - 1 && (
                      <div className="flex-1 h-px" style={{ background: 'var(--shell-surface)' }} />
                    )}
                  </motion.div>
                ))}
              </div>
              <p className="text-[13px] mt-3 font-medium" style={{ color: 'var(--shell-text-secondary, var(--shell-text))', fontFamily: '"Inter Tight", sans-serif' }}>
                {story.repair.result}
              </p>
            </div>
            <div className="mt-4 flex items-end justify-between">
              <span className="-rotate-1 leading-none" style={{ fontFamily: '"Caveat", cursive', fontSize: 14, color: RED }}>
                intent became arrival.
              </span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); nextState(); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-medium tracking-[0.02em] transition-all duration-200 hover:shadow-sm active:scale-[0.97]"
                style={{
                  background: 'transparent',
                  color: GREEN,
                  border: `1px solid ${GREEN}60`,
                }}
                data-testid={`replay-${story.id}`}
              >
                Replay
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" />
                </svg>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ═══ MAIN SECTION ═══ */
export default function PromiseArrivalSection() {
  const [openCaseIdx, setOpenCaseIdx] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const filteredCases = activeCategory === 'all'
    ? SCALE_CASES
    : SCALE_CASES.filter(fc => fc.category === activeCategory);
  return (
    <section
      className="relative overflow-hidden"
      style={{ background: 'var(--shell-bg, #050505)' }}
      data-testid="promise-arrival-section"
    >
      {/* Grain */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")`,
        opacity: 0.1, mixBlendMode: 'overlay',
      }} />

      {/* ═══ TOP: EMOTIONAL REVEAL ═══ */}
      <div className="relative z-[1] py-16 sm:py-24 px-6">
        <div className="max-w-[860px] mx-auto">
          {/* Header */}
          <motion.div
            className="mb-10 sm:mb-14"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] mb-4" style={{ color: `${RED}60` }}>
              From Promise to Arrival
            </p>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-[-0.03em] leading-[1.15] mb-3"
              style={{ color: 'var(--shell-text)', fontFamily: '"Inter Tight", sans-serif' }}
              data-testid="promise-arrival-heading"
            >
              What people were promised.
              <br />
              <span style={{ color: RED }}>What never arrived.</span>
            </h2>
            <p className="text-[13px] max-w-[520px] leading-[1.75]" style={{ color: 'var(--shell-text-secondary)', whiteSpace: 'pre-line' }}>
              {`Every card below was promised.
Every card below did not arrive.

Movement without arrival
is no longer possible.`}
            </p>
          </motion.div>

          {/* 3-state cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {STORIES.map((story, i) => (
              <StoryCard key={story.id} story={story} index={i} />
            ))}
          </div>

          {/* Closing beat */}
          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <p className="-rotate-1" style={{ fontFamily: '"Kalam", cursive', fontSize: 'clamp(20px, 4vw, 30px)', color: RED }} data-testid="arrival-emotional-beat">
              Intent arrives. Or it does not move at all.
            </p>
          </motion.div>
        </div>
      </div>

      {/* ═══ BOTTOM: INTERACTIVE SCALE GRID — click to reveal real-world use cases ═══ */}
      <div className="relative z-[1] pb-16 sm:pb-24 px-6">
        <div className="max-w-[1100px] mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="flex-1 h-px" style={{ background: 'var(--shell-border)' }} />
            <span className="text-[10px] font-mono uppercase tracking-[0.15em]" style={{ color: 'var(--shell-text-muted)' }}>
              Where truth governs movement at population scale
            </span>
            <div className="flex-1 h-px" style={{ background: 'var(--shell-border)' }} />
          </div>
          <p className="text-center text-[12px] mb-5" style={{ color: 'var(--shell-text-muted)' }}>
            Click any card to see the real-world scenario
          </p>

          {/* ═══ CATEGORY FILTER PILLS ═══ */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-8" role="tablist" aria-label="Filter use cases by category">
            {CATEGORIES.map((cat) => {
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => { setActiveCategory(cat.id); setOpenCaseIdx(null); }}
                  className="inline-flex items-center px-3.5 py-1.5 rounded-full text-[11.5px] font-medium transition-all duration-200 hover:shadow-sm active:scale-[0.97]"
                  style={{
                    background: isActive ? RED : 'var(--shell-surface)',
                    color: isActive ? '#FFFFFF' : 'var(--shell-text-secondary)',
                    border: `1px solid ${isActive ? RED : 'var(--shell-border)'}`,
                    boxShadow: isActive ? `0 1px 2px ${RED}30` : 'none',
                  }}
                  data-testid={`category-filter-${cat.id}`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" style={{ gridAutoRows: 'auto' }}>
            {filteredCases.map((fc, i) => {
              const cardKey = `${fc.category}-${fc.label}`;
              return (
                <ScaleCaseCard
                  key={cardKey}
                  fc={fc}
                  i={i}
                  isOpen={openCaseIdx === cardKey}
                  onToggle={() => setOpenCaseIdx(openCaseIdx === cardKey ? null : cardKey)}
                />
              );
            })}
          </div>

          {/* ═══ CFO-PAIN CONTRAST — Without Granullo vs With Granullo ═══ */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.6 }}
            className="mt-16 pt-10"
            style={{ borderTop: '1px dashed var(--shell-border)' }}
            data-testid="cfo-pain-contrast"
          >
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] mb-6 text-center" style={{ color: 'var(--shell-text-muted)' }}>
              From CFO pain to structural certainty
            </p>
            <div className="grid md:grid-cols-2 gap-4 max-w-[920px] mx-auto">
              {/* WITHOUT */}
              <motion.div
                whileHover={{ y: -2 }}
                className="rounded-xl p-5 relative overflow-hidden"
                style={{
                  background: `${RED}05`,
                  border: `1px solid ${RED}22`,
                  transition: 'all 220ms ease',
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: RED }} />
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] font-semibold" style={{ color: RED }}>
                    Without Granullo
                  </p>
                </div>
                <ul className="space-y-2 text-[12.5px] leading-[1.55]" style={{ color: 'var(--shell-text)' }}>
                  {[
                    'VAT paid late',
                    'Withholding errors',
                    'Supplier disputes',
                    'Hidden liabilities',
                    'Regulatory penalties',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="shrink-0 pt-1" style={{ color: `${RED}99` }}>✕</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* WITH */}
              <motion.div
                whileHover={{ y: -2 }}
                className="rounded-xl p-5 relative overflow-hidden"
                style={{
                  background: `${GREEN}06`,
                  border: `1px solid ${GREEN}2A`,
                  transition: 'all 220ms ease',
                  boxShadow: `0 0 24px ${GREEN}15`,
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: GREEN, boxShadow: `0 0 6px ${GREEN}` }} />
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] font-semibold" style={{ color: GREEN }}>
                    With Granullo
                  </p>
                </div>
                <ul className="space-y-2 text-[12.5px] leading-[1.55]" style={{ color: 'var(--shell-text)' }}>
                  {[
                    'Tax obligations reserved before payment',
                    'Withholding separated at source',
                    'No reconciliation panic',
                    'No invisible liability',
                    'No penalty risk to manage',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="shrink-0 pt-[2px]" style={{ color: GREEN }}>✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Closing theorem — category-defining line */}
            <motion.p
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="mt-10 text-center max-w-[820px] mx-auto"
              style={{
                fontFamily: '"Source Serif 4", Georgia, serif',
                fontSize: 'clamp(20px, 3vw, 28px)',
                lineHeight: 1.3,
                letterSpacing: '-0.015em',
                color: 'var(--shell-text)',
              }}
              data-testid="cfo-pain-closing"
            >
              We do not help you calculate VAT later.{' '}
              <span style={{ color: RED, fontWeight: 600 }}>
                We make it impossible to spend it incorrectly in the first place.
              </span>
            </motion.p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
